import React, { useState } from "react";
import "./CreditRiskIndicators.css";

interface KeyRiskIndicator {
  label: string;
  value: string;
}

interface PortfolioRow {
  rating: string;
  EL: string;
  EAD: string;
  perc: string;
}

interface TopExposureRow {
  name: string;
  EAD: number;
  EL: number;
  UL: number;
  RAG: string;
}

const CreditRiskIndicators: React.FC = () => {
  const [expandPortfolio, setExpandPortfolio] = useState<boolean>(true);
  const [expandTop5, setExpandTop5] = useState<boolean>(true);

  // Mock Data
  const keyRiskIndicators: KeyRiskIndicator[] = [
    { label: "Probability of default", value: "0.0" },
    { label: "Exposure at default", value: "0.0" },
    { label: "Loss given default", value: "0.0" },
    { label: "Expected loss", value: "0.0" },
  ];

  const portfolioData: PortfolioRow[] = [
    { rating: "Total", EL: "0.0", EAD: "0.0", perc: "0.0" },
    { rating: "A", EL: "0.0", EAD: "0.0", perc: "0.0" },
    { rating: "A-", EL: "0.0", EAD: "0.0", perc: "0.0" },
    { rating: "B+", EL: "0.0", EAD: "0.0", perc: "0.0" },
    { rating: "BB+", EL: "0.0", EAD: "0.0", perc: "0.0" },
    { rating: "CC", EL: "0.0", EAD: "0.0", perc: "0.0" },
    { rating: "NR", EL: "0.0", EAD: "0.0", perc: "0.0" },
  ];

  const topExposures: TopExposureRow[] = [
    { name: "Top 5 Total", EAD: 0.0, EL: 0.0, UL: 0.0, RAG: "" },
    { name: "Volkswagen ClimatePartner", EAD: 0.0, EL: 0.0, UL: 0.0, RAG: "" },
    { name: "CF Flex", EAD: 0.0, EL: 0.0, UL: 0.0, RAG: "" },
    { name: "RWE", EAD: 0.0, EL: 0.0, UL: 0.0, RAG: "" },
    { name: "George BV", EAD: 0.0, EL: 0.0, UL: 0.0, RAG: "" },
    { name: "Municipality Iasi", EAD: 0.0, EL: 0.0, UL: 0.0, RAG: "" },
  ];

  return (
    <div className="risk-dashboard">
      {/* Header */}
      <h2 style={{ color: "white", textAlign: "center"  }}>RMS Total</h2>

      {/* Key Risk Indicators */}
      <div className="indicators">
        {keyRiskIndicators.map((item: KeyRiskIndicator, idx: number) => (
          <div key={idx} className="indicator-card">
            <h3>{item.value}</h3>
            <p>{item.label}</p>
          </div>
        ))}
      </div>

      {/* Metrics Table */}
      <div className="metrics">
        <table>
          <thead>
            <tr>
              <th>Metric</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>Standalone Unexpected Loss (UL)</td><td>0.0</td></tr>
            <tr><td>Capital available</td><td>0.0</td></tr>
            <tr><td>Buffer vs UL</td><td>0.0</td></tr>
            <tr><td>Top 5 Counterparty Exposure</td><td>0.0</td></tr>
          </tbody>
        </table>
      </div>

      {/* Portfolio Breakdown */}
      <div className="section">
        <div
          className="section-header"
          onClick={() => setExpandPortfolio(!expandPortfolio)}
        >
          <h3>Portfolio Breakdown by Rating</h3>
          <span>{expandPortfolio ? "\u25B2" : "\u25BC"}</span>
        </div>
        {expandPortfolio && (
          <div className="section-content">
            <p style={{ color: "white" }}>Rating concentration (HHI): 0.0</p>
            <table>
              <thead>
                <tr>
                  <th>Rating</th>
                  <th>EL</th>
                  <th>EAD</th>
                  <th>% EAD</th>
                </tr>
              </thead>
              <tbody>
                {portfolioData.map((row: PortfolioRow, idx: number) => (
                  <tr key={idx}>
                    <td className={row.rating === "Total" ? "bold" : ""}>
                      {row.rating}
                    </td>
                    <td>{row.EL}</td>
                    <td>{row.EAD}</td>
                    <td>{row.perc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Top 5 Exposures */}
      <div className="section">
        <div
          className="section-header"
          onClick={() => setExpandTop5(!expandTop5)}
        >
          <h3>Top 5 Exposures</h3>
          <span>{expandTop5 ? "\u25B2" : "\u25BC"}</span>
        </div>
        {expandTop5 && (
          <div className="section-content">
            <table>
              <thead>
                <tr>
                  <th>Counterparty</th>
                  <th>EAD ({"\u20AC"}k)</th>
                  <th>EL ({"\u20AC"}k)</th>
                  <th>UL ({"\u20AC"}k)</th>
                  <th>RAG</th>
                </tr>
              </thead>
              <tbody>
                {topExposures.map((row: TopExposureRow, idx: number) => (
                  <tr key={idx}>
                    <td className={row.name === "Top 5 Total" ? "bold" : ""}>
                      {row.name}
                    </td>
                    <td>{row.EAD}</td>
                    <td>{row.EL}</td>
                    <td>{row.UL}</td>
                    <td>{row.RAG}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreditRiskIndicators;
